import type { FrenchNarrativeTone } from '../../types';
import type { CreditTopUpContext } from '../../types/creditTopUp';

export const MODERN_FRENCH_DIALOGUE_RULESET_VERSION = 'fr_dialogue_v5';
export const TRADITIONAL_FRENCH_DIALOGUE_RULESET_VERSION = 'fr_dialogue_v5_traditional';
export const FRENCH_GUILLEMETS_DASHES_RULESET_VERSION = 'fr_dialogue_v13_guillemets_dashes';
export const LEGACY_FRENCH_DIALOGUE_CLEANUP_RULESET_VERSION = 'fr_dialogue_v9_legacy_cleanup';
export const LEGACY_TRADITIONAL_FRENCH_DIALOGUE_RULESET_VERSION = 'fr_dialogue_v4_traditional';
export const PREVIOUS_TRADITIONAL_FRENCH_DIALOGUE_RULESET_VERSION = 'fr_dialogue_v3_traditional';
export const OLDEST_TRADITIONAL_FRENCH_DIALOGUE_RULESET_VERSION = 'fr_dialogue_v2_traditional';
export const OLDER_TRADITIONAL_FRENCH_DIALOGUE_RULESET_VERSION = 'fr_dialogue_v6_traditional';

export type FrenchDialogueRulesetOption = {
  version: string;
  label: string;
  description?: string;
  examplePattern?: string;
};

export type FrenchDialogueDiscountedRetranslationPreset = {
  name?: string;
  workflowMode?: string;
  modelId?: string;
  modelDisplayName?: string;
  quotationStyle?: string;
  frenchDialogueRulesetVersion?: string;
  frenchTone?: string;
  enableSystemProofreader?: boolean;
  requireCharacterAnalysis?: boolean;
  useBatch?: boolean;
  termScanPreflightDecision?: string;
  discountPercent?: number;
};

export type FrenchDialogueDiscountedRetranslationOffer = {
  eligible: boolean;
  reasonCode: string;
  reasonDetail?: string | null;
  requiredCredits?: number;
  discountedCredits?: number;
  discountPercent?: number;
  preset?: FrenchDialogueDiscountedRetranslationPreset;
  runId?: string | null;
  inProgress?: boolean;
  mode?: 'initial' | 'retry_failed_only';
  offerRootRunId?: string | null;
  attemptNumberNext?: number;
  chaptersTotalOriginal?: number;
  chaptersRemaining?: number;
  chaptersFailedRemaining?: number;
  chapterGuidsToQueue?: string[];
};

export type FrenchDialogueLegacyEligibility = {
  eligible: boolean;
  reasonCode: string;
  reasonDetail?: string | null;
  pair?: string;
  alreadyRan?: boolean;
  inProgress?: boolean;
  cutoffIso?: string;
  rulesetVersion?: string;
  chaptersTotal?: number;
  chaptersCompleted?: number;
  chaptersPending?: number;
  requestedChapterGuid?: string;
  requestedChapterCompleted?: boolean;
  availableRulesets?: FrenchDialogueRulesetOption[];
  rulesetEligibility?: Record<string, FrenchDialogueLegacyEligibility>;
  discountedRetranslation?: FrenchDialogueDiscountedRetranslationOffer;
};

export const FALLBACK_FRENCH_DIALOGUE_RULESET_OPTIONS: FrenchDialogueRulesetOption[] = [
  {
    version: MODERN_FRENCH_DIALOGUE_RULESET_VERSION,
    label: 'Updated French Dialog Rules',
    description:
      'Keeps narration and resumed same-speaker dialogue in one paragraph by default.',
    examplePattern: '— Texte. Action beat. Suite du texte.',
  },
  {
    version: TRADITIONAL_FRENCH_DIALOGUE_RULESET_VERSION,
    label: 'Traditional French Dialog Rules',
    description:
      'Separates narration/action beats into prose paragraphs and restarts resumed speech on a new em-dash line.',
    examplePattern: '— Texte.\nAction beat.\n— Suite du texte.',
  },
];

export const normalizeFrenchDialogueRulesetVersion = (value?: string | null): string => {
  const raw = String(value || '').trim();
  const normalized =
    raw === LEGACY_TRADITIONAL_FRENCH_DIALOGUE_RULESET_VERSION
      || raw === PREVIOUS_TRADITIONAL_FRENCH_DIALOGUE_RULESET_VERSION
      || raw === OLDEST_TRADITIONAL_FRENCH_DIALOGUE_RULESET_VERSION
      || raw === OLDER_TRADITIONAL_FRENCH_DIALOGUE_RULESET_VERSION
      ? TRADITIONAL_FRENCH_DIALOGUE_RULESET_VERSION
      : raw;
  if (!normalized) return TRADITIONAL_FRENCH_DIALOGUE_RULESET_VERSION;
  const supported = new Set(
    FALLBACK_FRENCH_DIALOGUE_RULESET_OPTIONS.map(option => String(option.version || '').trim()),
  );
  return supported.has(normalized) ? normalized : TRADITIONAL_FRENCH_DIALOGUE_RULESET_VERSION;
};

export const normalizeQuotationStyleValue = (value?: string | null): string =>
  String(value || '').trim().toLowerCase().replace(/[_-]+/g, ' ');

export const resolveFrenchDialogueRulesetForRequest = ({
  workflowMode,
  targetLang,
  quotationStyle,
  selectedRulesetVersion,
}: {
  workflowMode: string;
  targetLang: string;
  quotationStyle: string;
  selectedRulesetVersion?: string | null;
}): string | undefined => {
  const normalizedTarget = String(targetLang || '').trim().toLowerCase();
  const normalizedStyle = normalizeQuotationStyleValue(quotationStyle);
  const isFrenchGuillemetsAndDashes =
    normalizedTarget === 'french' && normalizedStyle === 'french guillemets and dashes';
  if (isFrenchGuillemetsAndDashes) return FRENCH_GUILLEMETS_DASHES_RULESET_VERSION;
  const isFrenchEmDash = normalizedTarget === 'french' && normalizedStyle === 'french em dash';
  if (!isFrenchEmDash) return undefined;
  const normalizedWorkflow = String(workflowMode || '').trim().toLowerCase();
  if (normalizedWorkflow === 'easy') return TRADITIONAL_FRENCH_DIALOGUE_RULESET_VERSION;
  return normalizeFrenchDialogueRulesetVersion(selectedRulesetVersion);
};

export const deriveFrenchDialogueRetranslationUiState = ({
  isChapterScopedRun,
  offer,
  availableCredits,
}: {
  isChapterScopedRun: boolean;
  offer?: FrenchDialogueDiscountedRetranslationOffer | null;
  availableCredits?: number | null;
}) => {
  const normalizedRequired = Number(offer?.requiredCredits);
  const normalizedDiscounted = Number(offer?.discountedCredits);
  const requiredCredits =
    Number.isFinite(normalizedRequired) && normalizedRequired > 0 ? Math.ceil(normalizedRequired) : 0;
  const discountedCredits =
    Number.isFinite(normalizedDiscounted) && normalizedDiscounted > 0
      ? Math.ceil(normalizedDiscounted)
      : 0;
  const normalizedAvailable =
    typeof availableCredits === 'number' && Number.isFinite(availableCredits)
      ? Math.max(0, Math.floor(availableCredits))
      : null;
  const shortfallCredits =
    normalizedAvailable == null ? 0 : Math.max(discountedCredits - normalizedAvailable, 0);
  return {
    showOption: !isChapterScopedRun,
    eligible: !isChapterScopedRun && Boolean(offer?.eligible),
    requiredCredits,
    discountedCredits,
    availableCredits: normalizedAvailable,
    hasEnoughCredits:
      !isChapterScopedRun && normalizedAvailable != null
        ? normalizedAvailable >= discountedCredits
        : null,
    shortfallCredits,
  };
};

export const buildFrenchDialogueRetranslationTopUpContext = ({
  discountedCredits,
  availableCredits,
}: {
  discountedCredits: number;
  availableCredits: number;
}): CreditTopUpContext => {
  const requiredCredits = Math.max(0, Math.ceil(Number(discountedCredits) || 0));
  const available = Math.max(0, Math.floor(Number(availableCredits) || 0));
  return {
    source: 'french_dialogue_retranslation',
    requiredCredits,
    availableCredits: available,
    shortfallCredits: Math.max(requiredCredits - available, 0),
  };
};

export const normalizeFrenchNarrativeTone = (
  value?: string | null,
): FrenchNarrativeTone | undefined => {
  const raw = (value || '').trim();
  if (!raw) return undefined;
  const collapsed = raw
    .toLowerCase()
    .replace(/[^a-z]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!collapsed) return undefined;
  if (collapsed.startsWith('modern') || collapsed.startsWith('accessible')) return 'modern';
  if (collapsed.startsWith('classical') || collapsed.startsWith('literary')) return 'classical';
  if (collapsed.startsWith('present')) return 'present';
  return undefined;
};
